import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { AnnahmenStandProjekt, ApiClient, VorgabeZeile } from '../../api';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, FilterBar, KpiCard, KpiReihe, LoadingState,
  StatusBadge, formatiereZahl, useFilterInUrl, type FilterDefinition, type Spalte,
} from '../../muster';
import type { Rahmen } from '../Projektrahmen';

/**
 * TL2: Heiz-/Kühllast → Annahmen und Vorgaben (TX-STANDARD S1.6, S3.4).
 *
 * ═══ Warum diese Seite ═══════════════════════════════════════════════════
 *
 * Bis TL2 ging Bestätigen nur per SQL, und die Vorbehalte der Läufe zählten
 * zu viel: „134 unbestätigte Parameter“ waren alle geladenen, „8.895
 * Raumwerte“ alle Profilwerte (Befund F-A6). Diese Seite zeigt EINE Liste:
 * die Vorgaben des Projekts mit Bestätigungsstand, und ob der maßgebende
 * Heiz- bzw. Kühllastlauf sie gelesen hat. Dieselbe Liste steht als
 * Annahmenblatt im Lastbericht.
 *
 * ═══ Bestätigen ═══════════════════════════════════════════════════════════
 *
 * Mit Begründung und Quelle (z. B. „Antwort HUL-15 vom …“), über ein
 * beschriftetes Feld, je Änderung eine Protokollzeile. Wer darf, entscheidet
 * der Server (vorläufig ProjectAdmin, bis HUL-24/QUE-13). Hausweite Vorgaben
 * gelten für alle Projekte und werden hier nicht bestätigt. Bestätigen ändert
 * keinen Wert und keinen gerechneten Lauf - der Lauf behält seinen Stand.
 */
export function AnnahmenVorgabenSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <AnnahmenVorgabenAnsicht api={api} projektId={projekt.id} />;
}

type Lade = { vorgaben: VorgabeZeile[]; rollen: string[]; stand: AnnahmenStandProjekt };

const ARTNAME: Record<string, string> = {
  parameter: 'Parameter', profil: 'Nutzungsprofil', ersatzkoerper: 'Ersatzkörper', zone: 'Zone',
  klima: 'Klima', standort: 'Standort', baustoff: 'Baustoff',
};

export const ANNAHMEN_FILTER: FilterDefinition[] = [
  {
    schluessel: 'art', beschriftung: 'Art', art: 'auswahl',
    optionen: Object.entries(ARTNAME).map(([wert, beschriftung]) => ({ wert, beschriftung })),
  },
  {
    schluessel: 'bestaetigung', beschriftung: 'Bestätigung', art: 'auswahl',
    optionen: [
      { wert: 'unbestaetigt', beschriftung: 'unbestätigt' },
      { wert: 'bestaetigt', beschriftung: 'bestätigt' },
    ],
  },
  {
    schluessel: 'lauf', beschriftung: 'Lauf', art: 'auswahl',
    hinweis: 'Nur Vorgaben, die der maßgebende Lauf gelesen hat (Momentaufnahme ab TL2).',
    optionen: [
      { wert: 'heizlast', beschriftung: 'genutzt im Heizlast-Auslegungslauf' },
      { wert: 'kuehllast', beschriftung: 'genutzt im Kühllast-Auslegungslauf' },
    ],
  },
];

/** Die Filter auf die Liste anwenden - ohne React, deshalb einzeln prüfbar. */
export function filtereVorgaben(zeilen: VorgabeZeile[], f: Record<string, unknown>): VorgabeZeile[] {
  return zeilen.filter((z) =>
    (!f.art || z.art === f.art)
    && (f.bestaetigung !== 'unbestaetigt' || !z.istBestaetigt)
    && (f.bestaetigung !== 'bestaetigt' || z.istBestaetigt)
    && (f.lauf !== 'heizlast' || z.genutztHeizlast === true)
    && (f.lauf !== 'kuehllast' || z.genutztKuehllast === true));
}

/** „TT.MM.JJJJ HH:MM UTC“ (S1.2) - ohne Laufzeitkultur, die Zeit kommt als UTC vom Server. */
export function datum(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const z = (n: number) => String(n).padStart(2, '0');
  return `${z(d.getUTCDate())}.${z(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} `
    + `${z(d.getUTCHours())}:${z(d.getUTCMinutes())} UTC`;
}

function Genutzt({ wert }: { wert: boolean | null }) {
  if (wert === null) return <span className="schwach">—</span>;
  return wert ? <span>ja</span> : <span className="schwach">nein</span>;
}

export function AnnahmenVorgabenAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<Lade | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<VorgabeZeile | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [werte, setWerte] = useFilterInUrl(ANNAHMEN_FILTER, 'a');

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const [v, s] = await Promise.all([api.heizlastVorgaben(projektId), api.heizlastAnnahmenStand(projektId)]);
      setDaten({ vorgaben: v?.vorgaben ?? [], rollen: v?.bestaetigenRollen ?? [], stand: s ?? { heizlast: null, kuehllast: null } });
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const gezeigt = useMemo(() => filtereVorgaben(daten?.vorgaben ?? [], werte), [daten, werte]);

  const h = daten?.stand.heizlast ?? null;
  const k = daten?.stand.kuehllast ?? null;
  const meta = daten
    ? [h ? `Heizlast Lauf ${h.laufnummer} · gerechnet ${datum(h.beendetAm)}` : 'Heizlast: kein maßgebender Lauf',
       k ? `Kühllast Lauf ${k.laufnummer} · gerechnet ${datum(k.beendetAm)}` : 'Kühllast: kein maßgebender Lauf'].join(' — ')
    : undefined;

  const spalten: Spalte<VorgabeZeile>[] = [
    { schluessel: 'art', titel: 'Art', wert: (z) => ARTNAME[z.art] ?? z.art },
    {
      schluessel: 'vorgabe', titel: 'Vorgabe', immerSichtbar: true,
      wert: (z) => `${z.bezeichnung ?? ''} ${z.schluessel}`,
      inhalt: (z) => (
        <span>{z.bezeichnung ?? z.schluessel}{z.bezeichnung && z.bezeichnung !== z.schluessel
          && <span className="schwach klein"> ({z.schluessel})</span>}</span>
      ),
    },
    {
      schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts',
      wert: (z) => z.zahlwert ?? z.wert,
      inhalt: (z) => (z.zahlwert !== null ? formatiereZahl(z.zahlwert) : (z.wert ?? '—')),
    },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit ?? '' },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => z.herkunft ?? '—' },
    {
      schluessel: 'bestaetigung', titel: 'Bestätigung', art: 'status',
      wert: (z) => (z.istBestaetigt ? 'bestätigt' : 'unbestätigt'),
      inhalt: (z) => (z.istBestaetigt
        ? <StatusBadge zustand="gut" text="bestätigt" klein />
        : <StatusBadge zustand="warnung" text="unbestätigt" klein />),
    },
    { schluessel: 'quelle', titel: 'Quelle/Norm', wert: (z) => z.quelle ?? '—', versteckt: true },
    { schluessel: 'bquelle', titel: 'bestätigt aufgrund', wert: (z) => z.bestaetigtQuelle ?? '—' },
    { schluessel: 'bam', titel: 'bestätigt am', wert: (z) => z.bestaetigtAm ?? '', inhalt: (z) => datum(z.bestaetigtAm), versteckt: true },
    { schluessel: 'heiz', titel: 'Heizlast', titelHinweis: 'im maßgebenden Heizlastlauf gelesen', wert: (z) => z.genutztHeizlast, inhalt: (z) => <Genutzt wert={z.genutztHeizlast} /> },
    { schluessel: 'kuehl', titel: 'Kühllast', titelHinweis: 'im maßgebenden Kühllastlauf gelesen', wert: (z) => z.genutztKuehllast, inhalt: (z) => <Genutzt wert={z.genutztKuehllast} /> },
    { schluessel: 'hausweit', titel: 'hausweit', wert: (z) => (z.hausweit ? 'ja' : 'nein') },
  ];

  const unbestaetigt = daten?.vorgaben.filter((v) => !v.istBestaetigt).length ?? 0;

  return (
    <>
      <PageHeader
        titel="Annahmen und Vorgaben"
        beschreibung="Welche Vorgaben Heiz- und Kühllast nutzen und ob sie bestätigt sind – bestätigen mit Begründung und Quelle."
        meta={meta ?? 'noch nicht geladen'}
      />
      {fehler && (
        <ErrorState titel="Die Vorgaben konnten nicht geladen werden." technisch={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      )}
      {!fehler && !daten && <LoadingState text="Vorgaben werden geladen …" />}
      {daten && (
        <>
          <Vorbehalte stand={daten.stand} />
          <KpiReihe>
            <KpiCard titel="Vorgaben" wert={daten.vorgaben.length} />
            <KpiCard titel="unbestätigt" wert={unbestaetigt} />
            <KpiCard titel="Heizlast: unbestätigt genutzt" wert={h?.unbestaetigt ?? null}
                     vergleich={h ? `Lauf ${h.laufnummer}${h.momentaufnahme ? '' : ', vor TL2'}` : 'kein Lauf'} />
            <KpiCard titel="Kühllast: unbestätigt genutzt" wert={k?.unbestaetigt ?? null}
                     vergleich={k ? `Lauf ${k.laufnummer}${k.momentaufnahme ? '' : ', vor TL2'}` : 'kein Lauf'} />
            <KpiCard titel="hausweit" wert={daten.vorgaben.filter((v) => v.hausweit).length}
                     vergleich="hier nicht bestätigbar" />
          </KpiReihe>
          <ContentSection titel="Vorgaben des Projekts"
                          beschreibung="Unbestätigte zuerst. Eine Zeile anklicken, um die Vorgabe zu bestätigen oder die Bestätigung zurückzunehmen.">
            <FilterBar filter={ANNAHMEN_FILTER} werte={werte} beiAenderung={setWerte} />
            <DataTable
              name={`Vorgaben (${gezeigt.length})`}
              spalten={spalten}
              zeilen={gezeigt}
              schluessel={(z) => `${z.art}|${z.id ?? z.schluessel}`}
              beiZeileKlick={(z) => setGewaehlt(z)}
              aktiveZeile={gewaehlt ? `${gewaehlt.art}|${gewaehlt.id ?? gewaehlt.schluessel}` : undefined}
              ausfuhr
              ausfuhrDateiname="annahmen-und-vorgaben.csv"
              dicht
              leer={(
                <EmptyState titel="Keine Vorgabe passt zum Filter."
                            erklaerung={daten.vorgaben.length === 0
                              ? 'Für dieses Projekt sind keine Vorgaben der Heiz- und Kühllast angelegt.'
                              : 'Filter zurücksetzen, um alle Vorgaben zu sehen.'} />
              )}
            />
            {gewaehlt && (
              <Bestaetigen api={api} projektId={projektId} vorgabe={gewaehlt} rollen={daten.rollen}
                           beiFertig={(m) => { setMeldung(m); setGewaehlt(null); void laden(); }} />
            )}
            <p aria-live="polite" className="schwach" data-test="meldung">{meldung}</p>
          </ContentSection>
          <Herleitung stand={daten.stand} />
        </>
      )}
    </>
  );
}

/** S1.2: der Hinweis „n unbestätigte Annahmen“ direkt unter dem Kopf. */
function Vorbehalte({ stand }: { stand: AnnahmenStandProjekt }) {
  const teile = [
    stand.heizlast && stand.heizlast.unbestaetigt > 0
      && `Heizlast Lauf ${stand.heizlast.laufnummer}: ${formatiereZahl(stand.heizlast.unbestaetigt)} unbestätigte Annahmen`,
    stand.kuehllast && stand.kuehllast.unbestaetigt > 0
      && `Kühllast Lauf ${stand.kuehllast.laufnummer}: ${formatiereZahl(stand.kuehllast.unbestaetigt)} unbestätigte Annahmen`,
  ].filter(Boolean) as string[];
  if (teile.length === 0) return null;
  return (
    <p className="hinweis hinweis-warnung" role="status" data-test="annahmen-vorbehalt">
      <StatusBadge zustand="warnung" text="unbestätigt" klein />{' '}{teile.join(' · ')}.
      {' '}Ein Bericht, der unbestätigte Vorgaben nutzt, bleibt Vorabzug (TX-STANDARD S3.4).
    </p>
  );
}

function Herleitung({ stand }: { stand: AnnahmenStandProjekt }) {
  const saetze = [...new Set([...(stand.heizlast?.herleitung ?? []), ...(stand.kuehllast?.herleitung ?? [])])];
  if (saetze.length === 0) return null;
  return (
    <ContentSection titel="Was gezählt ist" aufklappbar standardOffen={false}>
      {saetze.map((s) => <p key={s} className="klein">{s}</p>)}
    </ContentSection>
  );
}

/**
 * Bestätigen oder zurücknehmen - Begründung und Quelle sind Pflicht, die
 * Antwort des Servers steht darunter (aria-live). Kein `window.prompt`.
 */
export function Bestaetigen({ api, projektId, vorgabe, rollen, beiFertig }: {
  api: ApiClient; projektId: string; vorgabe: VorgabeZeile; rollen: string[]; beiFertig: (meldung: string) => void;
}) {
  const [begruendung, setBegruendung] = useState('');
  const [quelle, setQuelle] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => { setBegruendung(''); setQuelle(''); setFehler(null); setMeldung(null); }, [vorgabe]);

  const zuruecknehmen = vorgabe.istBestaetigt;
  const name = vorgabe.bezeichnung ?? vorgabe.schluessel;

  if (!vorgabe.bestaetigbar) {
    return (
      <div className="karte" data-test="bestaetigen">
        <h3>{name}</h3>
        <p className="klein">
          {vorgabe.hausweit
            ? 'Diese Vorgabe ist hausweit und gilt für alle Projekte. Bestätigt wird sie nicht aus einem Projekt heraus, '
              + 'sondern bis zur Entscheidung über HUL-24 als Datenschritt mit OK.'
            : vorgabe.art === 'zone'
              ? 'Die Zone hat keine aktiven Mitglieder ohne veraltetes Kennzeichen „verwaist“ – zuerst den Datenschritt zonen-heilen.'
              : 'Diese Vorgabe wird hier nicht bestätigt.'}
        </p>
      </div>
    );
  }

  const senden = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.vorgabeBestaetigen(projektId, {
        art: vorgabe.art, id: vorgabe.id, schluessel: vorgabe.art === 'zone' ? vorgabe.schluessel : null,
        begruendung: begruendung.trim(), quelle: quelle.trim(), zuruecknehmen,
      });
      setMeldung(a.message);
      beiFertig(a.message);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="karte" data-test="bestaetigen">
      <h3>{zuruecknehmen ? `Bestätigung zurücknehmen: ${name}` : `Bestätigen: ${name}`}</h3>
      {vorgabe.istBestaetigt && (
        <p className="klein">
          Bestätigt {vorgabe.bestaetigtVon ? `von ${vorgabe.bestaetigtVon} ` : ''}am {datum(vorgabe.bestaetigtAm)}
          {vorgabe.bestaetigtQuelle && <> aufgrund {vorgabe.bestaetigtQuelle}</>}
          {vorgabe.bestaetigtBegruendung && <>: {vorgabe.bestaetigtBegruendung}</>}
        </p>
      )}
      <div className="m-feldgitter">
        <Feld beschriftung="Begründung" art="textfeld" zeilen={2} wert={begruendung} pflicht breite="voll"
              hinweis="Steht an der Vorgabe und im Protokoll."
              beiAenderung={(w) => setBegruendung((w as string) ?? '')} />
        <Feld beschriftung="Quelle" wert={quelle} pflicht breite="voll"
              platzhalter="z. B. Antwort HUL-15 vom 20.10.2026"
              hinweis={`Worauf die Bestätigung beruht. Berechtigt: ${rollen.join(', ') || '—'}.`}
              beiAenderung={(w) => setQuelle((w as string) ?? '')} />
      </div>
      {fehler && <ErrorState klein titel="Nicht gespeichert." meldung={fehler} />}
      <button type="button" className="m-knopf m-knopf-haupt"
              disabled={laeuft || begruendung.trim() === '' || quelle.trim() === ''}
              onClick={() => { void senden(); }}>
        {laeuft ? 'wird gespeichert …' : zuruecknehmen ? 'Bestätigung zurücknehmen' : 'bestätigen'}
      </button>
      <span aria-live="polite" className="schwach">{meldung}</span>
    </div>
  );
}

/**
 * S1.2: Der Hinweis „n unbestätigte Annahmen“ für den Rahmen aller
 * Heiz-/Kühllastseiten, mit Verweis auf diese Seite. Lädt still: Ein Fehler
 * hier verdrängt nicht die Seite darunter.
 */
export function Annahmenhinweis({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<AnnahmenStandProjekt | null>(null);

  useEffect(() => {
    let aktiv = true;
    Promise.resolve()
      .then(() => api.heizlastAnnahmenStand(projektId))
      .then((s) => { if (aktiv) setStand(s ?? null); })
      .catch(() => { if (aktiv) setStand(null); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  const n = (stand?.heizlast?.unbestaetigt ?? 0) + (stand?.kuehllast?.unbestaetigt ?? 0);
  if (!stand || n === 0) return null;

  return (
    <p className="hinweis hinweis-warnung" role="status" data-test="annahmenhinweis">
      <StatusBadge zustand="warnung" text="unbestätigt" klein />{' '}
      {stand.heizlast && <>Heizlast Lauf {stand.heizlast.laufnummer}: {formatiereZahl(stand.heizlast.unbestaetigt)} unbestätigte Annahmen</>}
      {stand.heizlast && stand.kuehllast && ' · '}
      {stand.kuehllast && <>Kühllast Lauf {stand.kuehllast.laufnummer}: {formatiereZahl(stand.kuehllast.unbestaetigt)} unbestätigte Annahmen</>}
      {' — '}<Link to="annahmen">Annahmen und Vorgaben</Link>
    </p>
  );
}
